import "./SecondMenu.scss";
import React, {Suspense, useCallback, useContext, useEffect, useMemo} from "react";
import {Link, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Badge, Layout, Menu, Tooltip} from "antd";
import Icons from "@root/hydraExtension/Icons";
import ScrollBar from "../ScrollBar";
import {GlobalContext} from "@tools/globalContext";
import LoadingComponent from "@components/loadingComponent/LoadingComponent";
import useUserRights from "@hooks/useUserRights";
import useNotificationsCount from "@components/notifications/useNotificationsCount";
import defaultSettings from "@resources/defaultSettings";
import { EPageType } from "@extension/config/pagesConfig";
import type { SinglePageConfig, IPageConfig } from "@extension/config/pagesConfig";
import type { IMainMenuDecorator} from "@tools/CustomInterfaces";
import useMenuDynamicChildren from "@extension/useMenuDynamicChildren";

interface Props {
    config: IPageConfig,
    decorator: IMainMenuDecorator[],
    onClick: (id: string) => void
    // dynamicChildren: any
}

const SecondMenu: React.FC<Props> = (props) => {
    const {config, decorator, onClick} = props;
    const { t } = useTranslation();
    const navigate = useNavigate()
    const { sideBarCollapsed, collapseSidebar} = useContext(GlobalContext);
    const {navigatorVisibility} = useUserRights();
    const {getNotificationsCount} = useNotificationsCount({decorator});
    const dynamicChildren = useMenuDynamicChildren({onClick}) 
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const dynamicChildrenName = searchParams.get('type');

    const getPageUrl = useCallback((item : SinglePageConfig, path: string[]) => {
        if (item.url?.indexOf("/") === 0)
            return {pageUrlName: item.id, urlPath: item.url}

        const pageUrlName = item.url || item.id;
        const urlPath = `${config.rootUrl ? `/${config.rootUrl}` : ""}/${[...path, pageUrlName].join("/")}`;
        return {pageUrlName, urlPath};
    },[config, dynamicChildrenName]);

    const activePath = useMemo(() => {
        const rootSplit = (config.rootUrl || "").split("/").filter(i => i)
        return [...(pathname || "").split("/").filter(i => i).slice(rootSplit.length), dynamicChildrenName as string].filter(i => i)
    }, [pathname, config.rootUrl, dynamicChildrenName])

    const menuItems = useMemo(() => {
        const activeMainPage = activePath[0];
        const mainPageConfig = config.pages.find((item : SinglePageConfig) => item.id === activeMainPage)
        const decoratorBranch = decorator.find((item : IMainMenuDecorator) => item.id === activeMainPage)?.children;
        if (decoratorBranch && mainPageConfig) {
            return decoratorBranch.filter((item: any) => !item.noMenu && navigatorVisibility(item)).reduce((res: any[], item) => {
                const page = config.pages.find(field => field.id === item.id);
                if (page  && (![EPageType.tabs, EPageType.item].includes(page.type as EPageType) || item.showInMenu)) {
                    res.push(Object.assign({}, page, {children: item.children?.filter(item => !item.noMenu), showInMenu: item.showInMenu}))
                }
                return res;
            }, [])
        }
    }, [activePath, config, decorator])

    useEffect(() => {
        if (!menuItems?.length)
            collapseSidebar("none")
        else if(menuItems?.length && sideBarCollapsed)
            collapseSidebar(true)
    }, [menuItems, sideBarCollapsed])

    const onTitleClick = useCallback((item : SinglePageConfig, urlPath: string, pathname: string) => {
        if(item.noLink && item.customClick)
            return onClick(item.customClick)

        if (urlPath !== pathname || dynamicChildrenName) {
            navigate(urlPath)
        }
    }, [onClick, navigate, dynamicChildrenName])

    const renderMenuBranch = useCallback((items: any[], ids: string[], path? : string[]): any[] => {
        return (items || [])
            .filter((item: SinglePageConfig) => ![EPageType.tabs, EPageType.item]
            //@ts-ignore
            .includes(item.type as EPageType) || item.showInMenu)
            .reduce((res: any[], item : SinglePageConfig) => {
            const iconPath = item.icon || `menu.${item.id}`;
            const menuTextPath = item.text || `menu:${item.id}.title`;
            const {pageUrlName, urlPath} = getPageUrl(item, path || [])
            const badgeValue = getNotificationsCount(item.id as string);

            const directChildren = (item.children || []).filter(item => {
                const pageConfig = config.pages.find(it => it.id === item.id)
                //@ts-ignore
                return (![EPageType.tabs, EPageType.item].includes(pageConfig?.type as EPageType) || item.showInMenu) && !item.noMenu
            })
            let dynamicMenuComponents = [];

            if (!directChildren.length && !item.dynamicChildren) {
                res.push({
                    key: item.id,
                    icon: <Badge key={item.id} dot={!!badgeValue} offset={sideBarCollapsed ? [0, 7]: [3, -4]}>{Icons(iconPath)}</Badge>,
                    label: item.noLink ? t(menuTextPath) : <Link to={urlPath}>{t(menuTextPath)}</Link>
                })
                return res;
            }
            else if (item.dynamicChildren) {
                const dynamicData = dynamicChildren[item.dynamicChildren];
                if(!dynamicData?.loading && dynamicData?.items?.length) {
                    dynamicMenuComponents = dynamicData?.items?.map((child: any) => {
                        return {
                            key: child.key,
                            icon: <Badge key={child.id} dot={!!badgeValue} offset={sideBarCollapsed ? [0, 7]: [3, -4]}>{item.icon || Icons(iconPath)}</Badge>,
                            label: child.label,
                            ...child.children && {children: child.children}
                        }
                    })
                }
            }

            res.push({
                key: item.id,
                icon: <Badge key={item.id} dot={!!badgeValue} offset={sideBarCollapsed ? [0, 7]: [3, -4]}>{Icons(iconPath)}</Badge>,
                label: <Tooltip title={t(menuTextPath)}>{t(menuTextPath)}</Tooltip>,
                children: [...renderMenuBranch(directChildren, [...ids, item?.id || ""], (path || []).concat(pageUrlName as string).filter(i => i)), ...dynamicMenuComponents],
                onTitleClick: () => onTitleClick(item, urlPath, pathname)
            })

            return res
        }, [])
    }, [t, pathname, dynamicChildren, getPageUrl, onClick, onTitleClick, config, getNotificationsCount, sideBarCollapsed])

    const onCollapse = useCallback((collapsed : boolean) => {
        collapseSidebar(collapsed)
    }, [collapseSidebar]);

    return (menuItems?.length || 0) > 0 ? <Layout.Sider
            {...defaultSettings.secondMenuTheme && {theme: defaultSettings.secondMenuTheme as any}}
            className="secondMenu"
            collapsible= {defaultSettings.secondMenuCollapsible}
            collapsed={!!sideBarCollapsed}
            onCollapse={onCollapse}
            collapsedWidth={40}
        >
            <ScrollBar>
                <Menu
                    mode="inline"
                    selectedKeys={activePath}
                    defaultOpenKeys={activePath}
                    style={{ minHeight: "100%"}}
                    items={renderMenuBranch((menuItems || []), [activePath[0]], [activePath[0]])}
                    onClick={(item: any) => {
                        onClick(item.key)
                    }}
                />

            </ScrollBar>
        </Layout.Sider>
        : null
}

export default SecondMenu
